import type { Hunt } from './types'

/**
 * Placeholders the fixtures and the API mappers both use for "not set". The
 * em-dash comes from the DTO mappers, which write "—" rather than an empty
 * string when a field is absent.
 */
const BLANK = new Set(['', '—', '-'])

/**
 * The title a hunt is listed under on calendar surfaces: the name its planner
 * typed in the wizard, falling back to the team's name when they left it blank.
 *
 * Deliberately never derived from the huntable species. A hunt routinely targets
 * six or more of them (moose, red deer, fallow deer, roe deer, wild boar, fox),
 * so a species-based title got cramped and told the reader little — CLUB-582.
 *
 * `Hunt.location` is where the hunt name lives: the wehunt-web mappers assign
 * `dto.huntName` to it, so this reads the real planner-chosen name in production.
 */
export function huntDisplayName(hunt: Pick<Hunt, 'location' | 'team'>): string {
  const name = hunt.location?.trim() ?? ''
  return BLANK.has(name) ? hunt.team : name
}

/** Total head of game shot during the hunt, summed across its harvest rows. */
export function downedCount(hunt: Pick<Hunt, 'harvest'>): number {
  return hunt.harvest?.reduce((sum, row) => sum + row.count, 0) ?? 0
}

/** Game reported as seen but not shot. Absent data reads as none reported. */
export function observedCount(hunt: Pick<Hunt, 'observed'>): number {
  return hunt.observed ?? 0
}
